import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save } from 'lucide-react';
import adminApi from '../../api/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await adminApi.getSettings();
      setSettings(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleStatChange = (index, field, value) => {
    const newStats = [...settings.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setSettings(prev => ({ ...prev, stats: newStats }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await adminApi.updateSettings(settings);
      toast({
        title: "Success",
        description: "Settings updated successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-20">Loading settings...</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-black uppercase mb-2">Site Settings</h1>
        <p className="text-gray-600">Manage your website content and company information</p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-lg border-2 border-gray-200"
        >
          <h2 className="text-2xl font-black uppercase mb-6">Homepage Hero</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase mb-2 block">Main Headline</Label>
              <Input
                value={settings.hero_headline}
                onChange={(e) => handleChange('hero_headline', e.target.value)}
                className="border-2 border-black h-12 text-lg font-black"
                placeholder="FILMMAKING"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase mb-2 block">Sub Headline (Orange Text)</Label>
              <Input
                value={settings.hero_subheadline}
                onChange={(e) => handleChange('hero_subheadline', e.target.value)}
                className="border-2 border-black h-12 text-lg font-black"
                placeholder="THAT SELLS"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase mb-2 block">Description</Label>
              <Input
                value={settings.hero_description}
                onChange={(e) => handleChange('hero_description', e.target.value)}
                className="border-2 border-black h-12"
                placeholder="Commercial video production for businesses that demand results"
              />
            </div>
          </div>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-8 rounded-lg border-2 border-gray-200"
        >
          <h2 className="text-2xl font-black uppercase mb-6">Stats Grid</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {settings.stats.map((stat, index) => (
              <div key={index} className="border-2 border-gray-200 p-4 rounded">
                <Label className="text-xs font-bold uppercase mb-2 block">Stat {index + 1}</Label>
                <div className="flex gap-2">
                  <Input
                    value={stat.number}
                    onChange={(e) => handleStatChange(index, 'number', e.target.value)}
                    className="border-2 border-black h-10 font-black"
                    placeholder="200+"
                  />
                  <Input
                    value={stat.label}
                    onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                    className="border-2 border-black h-10"
                    placeholder="Projects"
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Company Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-8 rounded-lg border-2 border-gray-200"
        >
          <h2 className="text-2xl font-black uppercase mb-6">Company Information</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase mb-2 block">Company Name</Label>
              <Input
                value={settings.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="border-2 border-black h-12 font-black"
                placeholder="FILMCRAFT"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  className="border-2 border-black h-12"
                  placeholder="hello@filmcraft.com"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase mb-2 block">Phone</Label>
                <Input
                  value={settings.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  className="border-2 border-black h-12"
                  placeholder="(123) 456-7890"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase mb-2 block">Location</Label>
                <Input
                  value={settings.contact_location}
                  onChange={(e) => handleChange('contact_location', e.target.value)}
                  className="border-2 border-black h-12"
                  placeholder="Los Angeles, CA"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* About Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-8 rounded-lg border-2 border-gray-200"
        >
          <h2 className="text-2xl font-black uppercase mb-6">About Page Story</h2>
          <Textarea
            value={settings.about_story}
            onChange={(e) => handleChange('about_story', e.target.value)}
            className="border-2 border-black min-h-48"
            placeholder="Tell your company story here. This appears on the About page."
          />
        </motion.div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="bg-black text-white hover:bg-gray-900 font-bold px-8 py-6 text-base"
          >
            <Save className="mr-2" size={20} />
            {saving ? 'Saving...' : 'Save All Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;